// pages/DepositPage.js

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { PageLoader, LoadingSpinner } from '../components/common/Loading';
import { AuthContext } from '../context/AuthContext';
import { WalletAPI, NowPaymentsAPI } from '../services/api';
import { 
  FaBitcoin, 
  FaUpload, 
  FaCopy, 
  FaCheckCircle, 
  FaClock, 
  FaTimesCircle, 
  FaArrowLeft, 
  FaInfoCircle, 
  FaExternalLinkAlt,
  FaImage,
  FaFilePdf,
  FaTimes,
  FaBolt,
  FaWallet,
  FaQrcode
} from 'react-icons/fa';
import { FiAlertCircle } from 'react-icons/fi';

const DepositPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [depositInfo, setDepositInfo] = useState(null);
  const [depositHistory, setDepositHistory] = useState([]);
  const [formData, setFormData] = useState({
    amount: '',
    tx_hash: '',
    screenshot: null,
    screenshot_preview: null,
    screenshot_name: ''
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('nowpayments');
  const [showManual, setShowManual] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  
  // NOWPayments state
  const [nowPayments, setNowPayments] = useState({
    invoiceUrl: null,
    paymentId: null,
    address: null,
    amount: null,
    currency: 'BTC',
    qrCode: null,
    loading: false,
    error: null
  });
  const [selectedCurrency, setSelectedCurrency] = useState('btc');

  // Supported currencies
  const supportedCurrencies = [
    { value: 'btc', label: 'Bitcoin (BTC)', icon: '₿' },
    // { value: 'usdt', label: 'Tether (USDT)', icon: '₮' },
    // { value: 'eth', label: 'Ethereum (ETH)', icon: '⟠' },
    // { value: 'ltc', label: 'Litecoin (LTC)', icon: 'Ł' },
    // { value: 'xrp', label: 'Ripple (XRP)', icon: '✕' },
    // { value: 'doge', label: 'Dogecoin (DOGE)', icon: 'Ð' },
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDepositInfo();
    fetchDepositHistory();
  }, [user, navigate]);

  const fetchDepositInfo = async () => {
    try {
      setLoading(true);
      const response = await WalletAPI.getDepositAddress();
      if (response && response.success !== false) {
        setDepositInfo(response);
      }
    } catch (error) {
      // console.error('Error fetching deposit info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepositHistory = async () => {
    try {
      const response = await WalletAPI.getUserDepositProofs();
      if (response && response.success !== false) {
        setDepositHistory(response.deposits || []);
      }
    } catch (error) {
      // console.error('Error fetching deposit history:', error);
    }
  };

  // NOWPayments - Create deposit
  const handleNowPaymentsDeposit = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setErrors({ amount: 'Please enter a valid amount' });
      return;
    }

    setNowPayments({ ...nowPayments, loading: true, error: null });
    setErrors({});

    try {
      const response = await NowPaymentsAPI.createDeposit({
        amount: parseFloat(formData.amount),
        currency: selectedCurrency
      });

      if (response.success) {
        setNowPayments({
          invoiceUrl: response.invoice_url || response.data?.invoice_url,
          paymentId: response.payment_id || response.data?.payment_id,
          address: response.address || response.data?.address,
          amount: response.amount || response.data?.amount,
          currency: (response.currency || response.data?.currency || 'BTC').toUpperCase(),
          qrCode: response.qr_code || response.data?.qr_code,
          loading: false,
          error: null
        });
        setShowInvoice(true);
        setSuccessMessage('✅ Invoice created! Complete the payment to credit instantly.');
      } else {
        throw new Error(response.error || 'Failed to create deposit');
      }
    } catch (error) {
      console.error('NOWPayments deposit error:', error);
      setNowPayments({
        ...nowPayments,
        loading: false,
        error: error.message || 'Failed to create deposit'
      });
    }
  };

  // Manual deposit handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, screenshot: 'Please upload a JPG, PNG, GIF, or PDF file' }));
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, screenshot: 'File size must be less than 5MB' }));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        screenshot: file,
        screenshot_preview: URL.createObjectURL(file),
        screenshot_name: file.name
      }));
      setErrors(prev => ({ ...prev, screenshot: '' }));
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      screenshot: null,
      screenshot_preview: null,
      screenshot_name: ''
    }));
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    if (!formData.screenshot) {
      newErrors.screenshot = 'Please upload proof of payment';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setSubmitting(true);
    setErrors(prev => ({ ...prev, submit: '' }));
    
    try {
      let screenshotPath = '';
      if (formData.screenshot) {
        const uploadResponse = await WalletAPI.uploadDepositScreenshot(formData.screenshot);
        if (uploadResponse && uploadResponse.success !== false) {
          screenshotPath = uploadResponse.screenshot_path;
        } else {
          throw new Error(uploadResponse?.error || 'Failed to upload screenshot');
        }
      }
      
      const depositData = {
        amount: parseFloat(formData.amount),
        tx_hash: formData.tx_hash || '',
        screenshot: screenshotPath
      };
      
      const response = await WalletAPI.submitDepositProof(depositData);
      
      if (response && response.success !== false) {
        setSuccessMessage('✅ Deposit proof submitted! Admin will verify shortly.');
        setFormData({
          amount: '',
          tx_hash: '',
          screenshot: null,
          screenshot_preview: null,
          screenshot_name: ''
        });
        fetchDepositHistory();
        setTimeout(() => setSuccessMessage(''), 10000);
      } else {
        throw new Error(response?.error || 'Failed to submit deposit proof');
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, submit: error.message || 'Failed to submit deposit proof' }));
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: <FaClock className="w-4 h-4" />, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Pending' },
      completed: { icon: <FaCheckCircle className="w-4 h-4" />, color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Completed' },
      rejected: { icon: <FaTimesCircle className="w-4 h-4" />, color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Rejected' }
    };
    const configItem = config[status] || config.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${configItem.color} flex items-center gap-1.5 inline-flex`}>
        {configItem.icon}
        {configItem.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black pt-16">
        <Header />
        <PageLoader text="Loading deposit information..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/wallet')}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FaArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">💎 Deposit</h1>
            <p className="text-gray-400 text-sm">Instant crypto deposits via NOWPayments</p>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <p className="text-green-400 text-sm flex items-center gap-2">
              <FaCheckCircle className="w-5 h-5" />
              {successMessage}
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-800">
          <button
            onClick={() => setActiveTab('nowpayments')}
            className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'nowpayments'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <FaBolt className="inline" />
            Instant Deposit
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'history'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <FaClock className="inline mr-2" />
            History
          </button>
        </div>

        {activeTab === 'nowpayments' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <FaBolt className="text-blue-400" />
                  Instant Crypto Deposit
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full ml-2">
                    Auto-Credit
                  </span>
                </h3>

                {nowPayments.error && (
                  <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <FiAlertCircle className="w-5 h-5" />
                      {nowPayments.error}
                    </p>
                  </div>
                )}

                {!showInvoice ? (
                  <>
                    {/* Currency Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Select Currency
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {supportedCurrencies.map((curr) => (
                          <button
                            key={curr.value}
                            onClick={() => setSelectedCurrency(curr.value)}
                            className={`p-3 rounded-lg border text-center transition-all ${
                              selectedCurrency === curr.value
                                ? 'border-blue-500 bg-blue-500/20 text-white'
                                : 'border-gray-700 hover:border-gray-600 text-gray-400'
                            }`}
                          >
                            <span className="text-lg">{curr.icon}</span>
                            <div className="text-xs mt-1">{curr.label}</div>
                          </button>
                        ))}
                      </div>
                      <p className="text-gray-500 text-xs mt-2">
                        ⚡ Your deposit will be auto-converted and credited in BTC
                      </p>
                    </div>

                    {/* Amount Input */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Amount *
                      </label>
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        step="0.00000001"
                        min="0.0001"
                        placeholder="0.00000000"
                        className={`w-full px-4 py-3 bg-gray-800 text-white rounded-lg border ${
                          errors.amount ? 'border-red-500' : 'border-gray-700'
                        } focus:border-blue-500 focus:outline-none`}
                      />
                      {errors.amount && (
                        <p className="text-red-400 text-sm mt-1">{errors.amount}</p>
                      )}
                    </div>

                    <button
                      onClick={handleNowPaymentsDeposit}
                      disabled={nowPayments.loading || !formData.amount}
                      className={`w-full py-3 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                        nowPayments.loading || !formData.amount
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white shadow-lg hover:shadow-blue-500/30'
                      }`}
                    >
                      {nowPayments.loading ? (
                        <>
                          <LoadingSpinner size="sm" color="white" />
                          Creating Invoice...
                        </>
                      ) : (
                        <>
                          <FaBolt className="w-5 h-5" />
                          Generate Deposit Invoice
                        </>
                      )}
                    </button>

                    {/* Toggle Manual Deposit */}
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => setShowManual(!showManual)}
                        className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
                      >
                        {showManual ? 'Hide Manual Deposit' : 'Need manual deposit? Click here'}
                      </button>
                    </div>

                    {/* Manual Deposit Fallback */}
                    {showManual && (
                      <div className="mt-6 pt-6 border-t border-gray-800">
                        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-4">
                          <p className="text-yellow-400 text-sm flex items-center gap-2">
                            <FiAlertCircle className="w-5 h-5" />
                            Manual deposits require admin verification (24-48 hours)
                          </p>
                        </div>
                        <form onSubmit={handleManualSubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Amount (BTC) *
                            </label>
                            <input
                              type="number"
                              name="amount"
                              value={formData.amount}
                              onChange={handleInputChange}
                              step="0.00000001"
                              min="0.0001"
                              placeholder="0.00000000"
                              className={`w-full px-4 py-3 bg-gray-800 text-white rounded-lg border ${
                                errors.amount ? 'border-red-500' : 'border-gray-700'
                              } focus:border-blue-500 focus:outline-none`}
                            />
                            {errors.amount && (
                              <p className="text-red-400 text-sm mt-1">{errors.amount}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Transaction Hash (optional)
                            </label>
                            <input
                              type="text"
                              name="tx_hash"
                              value={formData.tx_hash}
                              onChange={handleInputChange}
                              placeholder="e.g., 1234abc..."
                              className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none font-mono"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Proof of Payment (Screenshot) *
                            </label>
                            <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                              errors.screenshot ? 'border-red-500' : 'border-gray-700'
                            } hover:border-blue-500 transition-colors`}>
                              {formData.screenshot_preview ? (
                                <div className="space-y-3">
                                  <div className="relative">
                                    {formData.screenshot.type === 'application/pdf' ? (
                                      <div className="flex items-center justify-center gap-2 p-4 bg-gray-800 rounded-lg">
                                        <FaFilePdf className="w-12 h-12 text-red-400" />
                                        <div className="text-left">
                                          <p className="text-white font-medium">{formData.screenshot_name}</p>
                                          <p className="text-gray-400 text-sm">
                                            {(formData.screenshot.size / 1024).toFixed(1)} KB
                                          </p>
                                        </div>
                                      </div>
                                    ) : (
                                      <img
                                        src={formData.screenshot_preview}
                                        alt="Screenshot preview"
                                        className="max-h-48 mx-auto rounded-lg object-contain"
                                      />
                                    )}
                                    <button
                                      type="button"
                                      onClick={handleRemoveFile}
                                      className="absolute -top-2 -right-2 p-1 bg-red-600 hover:bg-red-700 rounded-full text-white transition-colors"
                                    >
                                      <FaTimes className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => document.getElementById('screenshot-input').click()}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                                  >
                                    Change File
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <FaImage className="w-12 h-12 text-gray-600 mx-auto" />
                                  <div>
                                    <p className="text-gray-300">Drag and drop or click to upload</p>
                                    <p className="text-gray-500 text-sm">JPG, PNG, GIF, PDF (Max 5MB)</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => document.getElementById('screenshot-input').click()}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                                  >
                                    <FaUpload className="inline mr-2" />
                                    Choose File
                                  </button>
                                </div>
                              )}
                              <input
                                id="screenshot-input"
                                type="file"
                                accept=".jpg,.jpeg,.png,.gif,.pdf"
                                onChange={handleFileChange}
                                className="hidden"
                              />
                            </div>
                            {errors.screenshot && (
                              <p className="text-red-400 text-sm mt-1">{errors.screenshot}</p>
                            )}
                          </div>

                          <button
                            type="submit"
                            disabled={submitting}
                            className={`w-full py-3 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                              submitting
                                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white shadow-lg hover:shadow-yellow-500/30'
                            }`}
                          >
                            {submitting ? (
                              <>
                                <LoadingSpinner size="sm" color="white" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <FaUpload className="w-5 h-5" />
                                Submit Manual Deposit
                              </>
                            )}
                          </button>
                        </form>
                      </div>
                    )}
                  </>
                ) : (
                  // Invoice Display
                  <div className="space-y-4">
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                      <p className="text-green-400 text-sm flex items-center gap-2">
                        <FaCheckCircle className="w-5 h-5" />
                        Invoice created! Complete the payment below.
                      </p>
                    </div>

                    <div className="bg-gray-800 rounded-lg p-6 text-center">
                      {nowPayments.qrCode ? (
                        <img 
                          src={nowPayments.qrCode} 
                          alt="Payment QR Code" 
                          className="mx-auto w-48 h-48"
                        />
                      ) : (
                        <div className="w-48 h-48 mx-auto bg-gray-700 rounded-lg flex items-center justify-center">
                          <FaQrcode className="w-24 h-24 text-gray-500" />
                        </div>
                      )}
                      
                      <div className="mt-4">
                        <p className="text-gray-400 text-sm">Amount to send</p>
                        <p className="text-2xl font-bold text-white">
                          {nowPayments.amount} {nowPayments.currency}
                        </p>
                      </div>

                      {nowPayments.address && (
                        <div className="mt-4 bg-black/50 rounded-lg p-3 flex items-center gap-2">
                          <code className="text-yellow-400 text-sm break-all flex-1 font-mono">
                            {nowPayments.address}
                          </code>
                          <button
                            onClick={() => copyToClipboard(nowPayments.address)}
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                          >
                            <FaCopy className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      )}

                      <button
                        onClick={() => window.open(nowPayments.invoiceUrl, '_blank')}
                        className="mt-4 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <FaExternalLinkAlt className="w-4 h-4" />
                        Open Payment Widget
                      </button>

                      <button
                        onClick={() => {
                          setShowInvoice(false);
                          setNowPayments({ 
                            invoiceUrl: null, 
                            paymentId: null, 
                            address: null, 
                            amount: null, 
                            currency: 'BTC', 
                            qrCode: null, 
                            loading: false, 
                            error: null 
                          });
                        }}
                        className="mt-2 w-full py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                      >
                        Cancel / Start New Deposit
                      </button>
                    </div>

                    <div className="text-center text-gray-500 text-sm">
                      <p>⏳ Payment will be auto-detected and credited instantly</p>
                      <p className="text-xs mt-1">🔒 Secured by NOWPayments</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Quick Info */}
            <div className="space-y-6">
              <div className="bg-gray-900 rounded-xl p-6">
                <h4 className="font-bold text-white mb-4">⚡ Instant Benefits</h4>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <FaCheckCircle className="text-green-400 mt-1" />
                    <span className="text-gray-300">Auto-credit in seconds</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheckCircle className="text-green-400 mt-1" />
                    <span className="text-gray-300">Accept 6+ cryptocurrencies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheckCircle className="text-green-400 mt-1" />
                    <span className="text-gray-300">Auto-convert to BTC</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheckCircle className="text-green-400 mt-1" />
                    <span className="text-gray-300">No admin review needed</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h4 className="font-bold text-white mb-4">📋 Requirements</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Min Deposit:</span>
                    <span className="text-white">~0.0001 BTC</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Confirmations:</span>
                    <span className="text-white">2</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Network:</span>
                    <span className="text-white">{depositInfo?.network || 'testnet'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Processing Time:</span>
                    <span className="text-green-400">~1-5 minutes</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/wallet')}
                className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <FaArrowLeft className="w-4 h-4" />
                Back to Wallet
              </button>
            </div>
          </div>
        ) : (
          // History Tab
          <div className="bg-gray-900 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-6">📊 Deposit History</h3>
            
            {depositHistory.length === 0 ? (
              <div className="text-center py-12">
                <FaClock className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-400">No deposit history found</p>
                <p className="text-gray-500 text-sm mt-2">Your deposit submissions will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {depositHistory.map((deposit) => (
                  <div key={deposit.id} className="bg-gray-800 rounded-lg p-4 hover:bg-gray-750 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-bold text-white">
                            {parseFloat(deposit.amount).toFixed(8)} BTC
                          </span>
                          {getStatusBadge(deposit.status)}
                          {deposit.payment_provider === 'nowpayments' && (
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full">
                              Instant
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-400 space-y-1">
                          {deposit.tx_hash && (
                            <div className="flex items-center gap-2">
                              <span>TX:</span>
                              <code className="text-xs bg-black/30 px-2 py-1 rounded font-mono">
                                {deposit.tx_hash.substring(0, 16)}...
                              </code>
                              <button
                                onClick={() => copyToClipboard(deposit.tx_hash)}
                                className="text-gray-500 hover:text-white transition-colors"
                              >
                                <FaCopy className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-4">
                            <span>Submitted: {new Date(deposit.created_at).toLocaleString()}</span>
                            {deposit.processed_at && (
                              <span className="text-green-400">
                                Processed: {new Date(deposit.processed_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {deposit.screenshot_path && (
                        <a
                          href={`https://api.diceace21.com/${deposit.screenshot_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-white transition-colors flex items-center gap-2"
                        >
                          <FaImage className="w-4 h-4" />
                          View Proof
                        </a>
                      )}
                    </div>
                    {deposit.admin_notes && (
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <p className="text-sm text-gray-400">
                          <span className="font-medium">Admin Note:</span> {deposit.admin_notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default DepositPage;